import { useState, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { Spinner } from './components/UI'
import Sidebar from './components/Sidebar'
import Login from './pages/Login'
import InviteAccept from './pages/InviteAccept'
import { ProductResearch } from './pages/ProductResearch'
import { FunnelBuilding, Creatives, AdCopy, AdScheduling, CreativeRequests, AssetPrep } from './pages/Sections'
import { FBInfrastructure } from './pages/FBInfrastructure'
import { WinningProducts, UserManagement } from './pages/WinningAndUsers'
import Company from './pages/Company'
import Reports from './pages/Reports'
import Payroll from './pages/Payroll'
import Changelog from './pages/Changelog'
import ActivityLog from './pages/ActivityLog'
import Dashboard from './pages/Dashboard'
import PriceCalculator from './pages/PriceCalculator'
import PricingAgent from './pages/PricingAgent'
import { ErrorBoundary, hookGlobalErrors } from './components/ErrorBoundary'
import Resources from './pages/Resources'
import GeneralResources from './pages/GeneralResources'
import Settings from './pages/Settings'
import { ROLES } from './lib/constants'
import './styles/App.css'

function Inner() {
  const { user, loading, signOut } = useAuth()
  const [active, setActive] = useState(() => {
    try { return localStorage.getItem('active_section') || '__init__' } catch { return '__init__' }
  })
  useEffect(() => {
    try { if (active && active !== '__init__') localStorage.setItem('active_section', active) } catch {}
  }, [active])

  // Public invite route — check URL path
  const invitePath = window.location.pathname.match(/^\/invite\/([a-z0-9]+)/i)
  if (invitePath) return <InviteAccept token={invitePath[1]} />
  useEffect(() => {
    const handler = () => setActive('settings')
    window.addEventListener('open-settings', handler)
    return () => window.removeEventListener('open-settings', handler)
  }, [])

  if (loading) return <Spinner />
  if (!user) return <Login />

  const role = user.role
  const isOwner = role === 'owner'
  const roleInfo = ROLES[role] || ROLES.pending

  if (role === 'pending') return (
    <div className="pending-page">
      <div style={{fontSize:48}}>⏳</div>
      <h2>Account Pending</h2>
      <p>Your account is awaiting role assignment. Contact your administrator.</p>
      <button className="btn btn-ghost" onClick={signOut} style={{marginTop:12}}>Sign Out</button>
    </div>
  )

  // Build effective permissions
  const ALL_SECTION_IDS = ['products','funnel','creatives','adcopy','scheduling','creativerequests','winning','fbinfra','reports','company']
  const ROLE_PERMS = {
    owner:          { products:true, funnel:true, creatives:true, adcopy:true, scheduling:true, creativerequests:true, winning:true, fbinfra:true, reports:true, company:true },
    funnel_builder: { funnel:true, reports:true },
    creative_editor:{ creatives:true, reports:true },
    ad_copy_writer: { adcopy:true, reports:true },
    ad_scheduler:   { scheduling:true, reports:true },
    pending:        {},
    user:           {},
  }
  const explicitPerms = (typeof user.permissions === 'object' && user.permissions && Object.keys(user.permissions).length > 0)
    ? user.permissions : null
  const effectivePerms = explicitPerms || ROLE_PERMS[role] || {}
  const firstSection = ALL_SECTION_IDS.find(s => effectivePerms[s]) || (isOwner ? 'products' : null)

  // Resolve which section to show — bulletproof
  let section
  if (active === '__init__') {
    section = 'dashboard'
  } else if (isOwner) {
    section = active  // owner can see everything including users/settings
  } else {
    // non-owner: allow resources/general if they have the corresponding permission,
    // otherwise only sections they have permission for
    const hasAnyResourcesPerm = ALL_SECTION_IDS.some(s => {
      const p = effectivePerms[s]
      return p === true || (typeof p === 'object' && p && p.resources)
    })
    const hasGeneralPerm = (() => {
      const p = effectivePerms['general']
      return p === true || (typeof p === 'object' && p && p.view)
    })()

    if (active === 'dashboard') {
      section = 'dashboard'
    } else if (active === 'changelog') {
      section = 'changelog'
    } else if (active === 'resources' && hasAnyResourcesPerm) {
      section = 'resources'
    } else if (active === 'general' && hasGeneralPerm) {
      section = 'general'
    } else {
      section = effectivePerms[active] ? active : firstSection
    }
  }
  const empMarket = user.assigned_market

  const NoAccess = () => (
    <div style={{padding:40,color:'var(--text2)',textAlign:'center'}}>
      <div style={{fontSize:32,marginBottom:12}}>🔒</div>
      <div style={{fontSize:14,fontWeight:600}}>No sections assigned</div>
      <div style={{fontSize:12,marginTop:4}}>Please contact your administrator</div>
    </div>
  )

  const renderSection = () => {
    if (!section) return <NoAccess />
    switch (section) {
      case 'products':   return <ProductResearch />
      case 'funnel':     return <FunnelBuilding market={empMarket} />
      case 'creatives':  return <Creatives market={empMarket} />
      case 'assetprep':  return <AssetPrep market={empMarket} />
      case 'adcopy':     return <AdCopy market={empMarket} />
      case 'scheduling': return <AdScheduling market={empMarket} />
      case 'creativerequests': return <CreativeRequests market={empMarket} />
      case 'fbinfra': return <FBInfrastructure />
      case 'winning':    return <WinningProducts />
      case 'users':      return isOwner ? <UserManagement /> : <NoAccess />
      case 'settings':   return isOwner ? <Settings /> : <NoAccess />
      case 'company':    return isOwner ? <Company /> : <NoAccess />
      case 'reports':    return <Reports />
      case 'payroll':    return <Payroll />
      case 'dashboard':  return <Dashboard setActive={setActive} />
      case 'pricecalc':  return <PriceCalculator />
      case 'pricingagent': return <PricingAgent />
      case 'activitylog': return isOwner ? <ActivityLog /> : <NoAccess />
      case 'changelog':  return <Changelog />
      case 'resources':  return <Resources />
      case 'general':    return <GeneralResources />
      default:
        if (!firstSection) return <div style={{padding:40,color:'var(--text2)',textAlign:'center'}}><div style={{fontSize:32,marginBottom:12}}>🔒</div><div style={{fontSize:14,fontWeight:600}}>No sections assigned</div><div style={{fontSize:12,marginTop:4}}>Please contact your administrator</div></div>
        if (firstSection === 'products') return <ProductResearch />
        if (firstSection === 'funnel') return <FunnelBuilding market={empMarket} />
        if (firstSection === 'creatives') return <Creatives market={empMarket} />
        if (firstSection === 'adcopy') return <AdCopy market={empMarket} />
        if (firstSection === 'scheduling') return <AdScheduling market={empMarket} />
        if (firstSection === 'winning') return <WinningProducts />
        if (firstSection === 'reports') return <Reports />
        return <ProductResearch />
    }
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">AdOps Management</div>
        <div className="header-right">
          <span className="header-user">{user.full_name || user.email}</span>
          <span className="header-role">{roleInfo.label}</span>

          <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign Out</button>
        </div>
      </header>
      <div className="layout">
        <Sidebar active={section} setActive={v => setActive(v)} effectivePerms={effectivePerms} isOwner={isOwner} />
        <main className="main">
          <div className="content"><ErrorBoundary section={section}>{renderSection()}</ErrorBoundary></div>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  hookGlobalErrors()
  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={{duration:3000}} />
      <Inner />
    </AuthProvider>
  )
}
